import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../../api/auth";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";
import Loader from "../../components/Loader";
import logo from "../../assets/logo.png";
import signUpLayer from "../../assets/sign_up_layer.png";

const Login: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setFormData((prev) => ({ ...prev, email: storedEmail }));
      localStorage.removeItem("email");
    }
  }, []);

  // validation
  const validate = () => {
    let tempErrors: any = {};
    if (!formData.email) tempErrors.email = "Email is required";
    if (!formData.password) tempErrors.password = "Password is required";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await loginUser(formData);
      // Expecting: { statusCode: 200, message, data: { access_token, userId, role } }
      const token = res?.data?.data?.access_token || res?.data?.access_token;
      const User = res?.data?.data?.userId || res?.data?.userId;
      const role = res?.data?.data?.role || res?.data?.role;
      
      if (token && User) {
        localStorage.setItem("access_token", token);
        localStorage.setItem("userId", User);
        if (role) {
          localStorage.setItem("userRole", role);
        }
      }

      // navigate after success based on role
      setTimeout(() => {
        if (role === "ADMIN") {
          navigate("/admin");
        } else if (role === "DOCTOR") {
          navigate("/dashboard");
        } else {
          // Default fallback
          navigate("/admin");
        }
      }, 1);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Login failed!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen bg-center bg-cover p-6"
      style={{ backgroundImage: `url(${signUpLayer})` }}
    >
      {loading && <Loader />}
      {/* ✅ Logo above card */}
      <div className="flex justify-center mb-3">
        <img src={logo} alt="Orthodontic Logo" className="h-24 w-auto object-contain" />
      </div>
      <div className="w-full max-w-md bg-white shadow-md rounded-2xl p-6">
        <h1 className="brand-heading text-center mb-6">Login</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div>
            <label className="block mb-1 text-sm font-medium">Email</label>
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block mb-1 text-sm font-medium">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                className="w-full px-3 pr-11 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm">{errors.password}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-[#016683] text-white py-2 rounded-lg hover:opacity-90 transition"
          >
            Login
          </button>
        </form>

        {/* Link */}
        <p className="mt-4 text-center text-sm">
          Don’t have an account?{" "}
          <Link to="/register" className="text-blue-500 hover:underline">
            Register
          </Link>
        </p>
        <p className="mt-4 text-center text-sm">
          Forgot Password?{" "}
          <Link to="/forgot-password" className="text-blue-500 hover:underline">
            Click here
          </Link>
        </p>
      </div>
      <ToastContainer />
    </div>
  );
};

export default Login;
